import {
  Component,
  computed,
  HostListener,
  inject,
  signal
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faBars,
  faBell,
  faChevronDown,
  faChevronRight,
  faCircleHalfStroke,
  faGlobe,
  faKey,
  faMoon,
  faQuestionCircle,
  faSearch,
  faShieldHalved,
  faShuffle,
  faSignOutAlt,
  faSun,
  faUser
} from '@fortawesome/free-solid-svg-icons';

import { AuthStore } from '@twist/core';
import { LayoutStore } from '@twist/ui-core';
import { AvatarComponent } from '@twist/ui/components';
import { TwModalService } from '@twist/ui-overlays';
import {
  TwPopoverComponent,
  TwPopoverContentComponent,
  TwPopoverTriggerDirective
} from '@twist/ui-popovers';

import { NavCommandPaletteComponent } from '../nav-command-palette/nav-command-palette.component';

/**
 * Toolbar / topbar nav.
 *
 * TWS-108 Wave 8 (Portal Chrome Restyle): the bespoke
 * `cdkConnectedOverlay` wiring around the user dropdown was
 * retired in favour of the shared `<tw-popover>` primitive
 * (TWS-122). The panel now consumes `<tw-popover-content>` with
 * per-instance size/padding overrides; surface, anchor
 * positioning, light-dismiss, top layer, and Escape handling
 * all come from the primitive.
 *
 * Notifications popover + Help button removed from the template
 * (no backend wiring exists yet — see template comment). The
 * mutual-exclusion `effect()` that coordinated notifOpen vs
 * userOpen has been dropped along with `notifOpen` — only one
 * popover remains, so coordination is moot.
 */
@Component({
  standalone: true,
  selector: 'app-toolbar-nav',
  imports: [
    FaIconComponent,
    RouterLink,
    AvatarComponent,
    TwPopoverComponent,
    TwPopoverContentComponent,
    TwPopoverTriggerDirective
  ],
  templateUrl: './toolbar-nav.component.html'
})
export class ToolbarNavComponent {
  private readonly _authStore = inject(AuthStore);
  protected readonly layoutStore = inject(LayoutStore);
  private readonly _modalService = inject(TwModalService);

  /** Guards against opening a second palette while one is already open. */
  private _paletteOpen = false;

  // ---------- user info (derived from AuthStore) ----------
  protected readonly authenticatedUser = this._authStore.user;
  protected readonly organization = this._authStore.organizationAuthInfo;
  /**
   * Theme selection is gated to Twist platform admins
   * (`OrganizationTypes.TwistAdmin`). Drives the `@if` around the
   * theme-toggle button in the template — non-admins do not see it
   * and are pinned to the dark theme by `LayoutStore` itself.
   */
  protected readonly isTwistAdmin = this._authStore.isTwistAdmin;
  protected readonly userOrganizations = computed(
    () => this._authStore.userAuthInfo()?.organizations ?? []
  );
  protected readonly hasMultipleOrganizations = computed(
    () => this.userOrganizations().length > 1
  );

  protected readonly userName = computed(() => this.authenticatedUser()?.name ?? '');
  protected readonly userEmail = computed(() => this.authenticatedUser()?.email ?? '');
  protected readonly userOrg = computed(() => {
    const user = this.authenticatedUser();
    const org = this.organization();
    if (!user) return '';
    if (!org) return user.name;
    return org.name;
  });
  protected readonly userInitials = computed(() => {
    const name = this.authenticatedUser()?.name ?? '';
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).slice(0, 2);
    return parts.map(p => p.charAt(0).toUpperCase()).join('');
  });
  protected readonly orgInitial = computed(() => {
    const org = this.organization()?.name ?? '';
    return org.charAt(0).toUpperCase() || 'T';
  });
  protected readonly orgMeta = computed(() => {
    // Placeholder line — the real authorization context doesn't yet expose
    // installation counts / plan tier. Replace in a later feature.
    const count = this.userOrganizations().length;
    return count > 1 ? `${count} organizations available` : 'Primary organization';
  });

  // ---------- popover state ----------
  //
  // Only the user-menu popover remains; the notifications
  // popover was removed pending real backend wiring. The
  // `<tw-popover>` primitive owns its own `[(open)]` — we keep
  // this model signal so methods (closeUser, switchOrganization,
  // Escape handler) can drive it programmatically.
  protected readonly userOpen = signal(false);

  // ---------- theme-toggle a11y label ----------
  protected readonly themeToggleLabel = computed(() => {
    const current = this.layoutStore.theme();
    // Label the NEXT step in the cycle (dark → light → system → dark).
    switch (current) {
      case 'dark':   return 'Switch to light theme';
      case 'light':  return 'Switch to system theme';
      case 'system': return 'Switch to dark theme';
    }
  });

  // ---------- methods ----------

  protected closeUser(): void {
    this.userOpen.set(false);
  }

  /**
   * Hamburger button handler. Below the mobile breakpoint the sidebar is
   * force-hidden (see `LayoutStore.effectiveSidebarState`) and the mobile
   * dialog takes over — open it. On desktop the hamburger toggles between
   * `full` and `rail`; the `hidden` state is mobile-only and intentionally
   * unreachable from this control.
   */
  protected onHamburger(): void {
    if (this.layoutStore.viewportBelowBreakpoint()) {
      this.layoutStore.openMobile();
      return;
    }
    this.layoutStore.cycleSidebarState();
  }

  /** Original auth-store logout call — preserved from the legacy dropdown. */
  protected logout(): void {
    this._authStore.logout();
  }

  protected switchOrganization(key: string): void {
    this._authStore.switchOrganization(key);
    this.closeUser();
  }

  // ---------- global keyboard dismiss (Escape) ----------
  //
  // The popover primitive's native `[popover]` API already wires
  // light-dismiss + Escape — but only when the popover has top-layer
  // focus. Closing on Escape from anywhere on the document is a
  // belt-and-braces extra, retained from the pre-migration handler.
  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.userOpen()) this.userOpen.set(false);
  }

  // ---------- ⌘K / Ctrl+K command palette ----------
  //
  // Global shortcut: Cmd+K (macOS) / Ctrl+K (Windows/Linux) opens the
  // navigation command palette. Listens on the document so the shortcut
  // works regardless of focus.
  @HostListener('document:keydown', ['$event'])
  protected onGlobalKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.openCommandPalette();
    }
  }

  /**
   * Opens the navigation command palette as a modal. Re-entrancy is
   * guarded so a held shortcut / double-click can't stack palettes;
   * the guard resets when the modal's result settles (close or dismiss).
   */
  protected openCommandPalette(): void {
    if (this._paletteOpen) {
      return;
    }
    this._paletteOpen = true;
    const ref = this._modalService.open<NavCommandPaletteComponent, void>(
      NavCommandPaletteComponent,
      { size: 'md', closedby: 'any' },
    );
    void ref.result.catch(() => undefined).finally(() => {
      this._paletteOpen = false;
    });
  }

  // ---------- FontAwesome icon references ----------
  protected readonly faBars = faBars;
  protected readonly faSearch = faSearch;
  protected readonly faQuestionCircle = faQuestionCircle;
  protected readonly faBell = faBell;
  protected readonly faMoon = faMoon;
  protected readonly faSun = faSun;
  protected readonly faCircleHalfStroke = faCircleHalfStroke;
  protected readonly faChevronDown = faChevronDown;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faUser = faUser;
  protected readonly faShieldHalved = faShieldHalved;
  protected readonly faKey = faKey;
  protected readonly faGlobe = faGlobe;
  protected readonly faSignOutAlt = faSignOutAlt;
  protected readonly faShuffle = faShuffle;
}
